<template>
    <div class="metric-filter-builder">
        <div class="row">
            <div class="col-6 mb-3">
                <label class="form-label">Display Name</label>
                <input 
                    type="text" 
                    id="name-input" 
                    class="form-control" 
                    v-model="local_mf_selected.name"
                    :class="{ 'is-invalid': errors.admin_name !== '' }" 
                    @change="clearErrorForField('name')"
                 aria-label="Display Name">
                <div class="text-danger invalid-feedback" v-if="errors.name !== ''">
                    {{ errors.name }}
                </div>
            </div>
            <div class="col-6 mb-3">
                <label class="form-label">Admin Name</label>
                <input 
                    type="text" 
                    id="name-input" 
                    class="form-control" 
                    v-model="local_mf_selected.admin_name"
                    :class="{ 'is-invalid': errors.admin_name !== '' }" 
                    @change="clearErrorForField('admin_name')"
                 aria-label="Admin Name">
                <div class="text-danger invalid-feedback" v-if="errors.admin_name !== ''">
                    {{ errors.admin_name }}
                </div>
            </div>

            <div v-if="type.toLowerCase() === 'metric'" class="col-6">
                <div class="mb-3">
                    <label class="form-label">Format</label>
                    <select 
                        class="form-select" 
                        :class="{ 'is-invalid': errors.format !== '' }"
                        id="format-select" 
                        v-model="local_mf_selected.format" 
                        @change="selectFormat(local_mf_selected.format)"
                     aria-label="Format">
                        <option value="" disabled selected>Select the format...</option>
                        <option value="date">Date</option>
                        <option value="money">Money</option>
                        <option value="number">Number</option>
                        <option value="percent">Percent</option>
                        <option value="string">String</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.format !== ''">
                        {{ errors.format }}
                    </div>
                </div>
            </div>
            <div v-if="type.toLowerCase() === 'metric'" class="col-6">
                <div class="mb-3">
                    <label class="form-label">Preferred Color (suggested based on Format type)</label>
                    <select 
                        class="form-select" 
                        :class="{ 'is-invalid': errors.color !== '' }"
                        id="color-select" 
                        v-model="local_mf_selected.color" 
                        @change="clearErrorForField('color')"
                     aria-label="Preferred Color (suggested based on Format type)">
                        <option value="" disabled selected>Select the color...</option>
                        <option value="yellow">Yellow</option>
                        <option value="green">Green</option>
                        <option value="blue">Blue</option>
                        <option value="purple">Red</option>
                    </select>
                    <div class="text-danger invalid-feedback" v-if="errors.color !== ''">
                        {{ errors.color }}
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col mb-3">
                <label class="form-label">Description</label>
                <input 
                    class="form-control"
                    name="mf_description" 
                    v-model="local_mf_selected.description" aria-label="Description">
            </div>
            <div class="col-3 mb-3" v-if="type.toLowerCase() === 'metric'" >
                <label class="form-label">
                    Importance 
                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" 
                        title="Rate the importance of this metric with regards to a fundraising program on a scall from 1 to 5" alt=""
                    ></img >
                </label>
                <select 
                    class="form-select" 
                    id="importance" 
                    v-model="local_mf_selected.importance"
                 aria-label="Importance">
                    <option value="0.1">1 - Not Very Important</option>
                    <option value="0.3">2</option>
                    <option value="0.5">3</option>
                    <option value="0.7">4</option>
                    <option value="0.9">5 - Most Important</option>
                </select>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="mb-3">
                    <label class="form-label">
                        Formula (<small>Use <code v-pre>&lbrace;&lbrace;client.id&rbrace;&rbrace;</code> to insert the client ID into the formula</small>)
                    </label>
                    <div class="input-group">
                        <textarea 
                            class="full-height form-control formula-input" 
                            @change="validateFormula" 
                            @input="resizeTextarea" 
                            v-model="local_mf_selected.formula"
                            name="mf_description" 
                            :class="{
                                'is-invalid': errors.formula !== '', 
                                'is-valid': errors.formula === '' && local_mf_selected.formula.trim() !== '' && !is_validating, 
                                'is-validating': is_validating
                            }"
                        />
                        <v-select 
                            id="formula-column-toggle" 
                            :options="local_columns" 
                            v-model="column_selected"
                            :searchable="true" 
                            class="searchable-select column-display category" 
                            placeholder="Columns"
                            :selectable="(option) => option.text !== ''"
                            label="text" 
                            @search="fetchOptions"
                            @update:modelValue="addColumnToText(column_selected)"
                            :filterable="false"
                            @open="handleColumnDisplayOpen"
                        >
                            <template #selected-option="{ text, category, subcategory }">
                                {{(category != null && category != "" && category != "list-header" ? category + ": " : "")}}{{subcategory != null && subcategory != "" ? subcategory + " › " : ""}}{{text}}
                            </template>

                            <template #open-indicator="{ attributes }">
                                <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                            </template>

                            <template #option="{ text, category, subcategory }">
                                <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
                                    v-on:click="expandCategory(category, null)"
                                    @mouseover="handleHover($event)"
                                    data-isexpanded="false">
                                    {{ category }} <i class="fas fa-caret-right"></i>
                                </div>
                                <div v-else-if="text == '' && subcategory"
                                    :data-subcategory="category"
                                    :data-subcategory-name="subcategory"
                                    class="subcategory-header"
                                    @click="expandSubcategory(category, subcategory, null)"
                                    @mouseover="handleHover($event)"
                                    data-isexpanded="false"
                                    v-show="isSubcategoryVisible(category, subcategory)">
                                    {{ subcategory }} <i class="fas fa-caret-right"></i>
                                </div>
                                <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
                                    :class="shouldShowOption(category, subcategory)"
                                    @mouseover="handleHover($event)">
                                    {{getDisplayText(text)}}
                                </div>
                            </template>
                            <template #no-options="{ search, searching, loading }">
                                <div class="suboption show" v-if="is_loading">
                                    <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
                                </div>
                                <div class="suboption show" v-else>
                                    <em>No results found</em>
                                </div>
                            </template>
                        </v-select>
                    </div>

                    <div class="text-danger invalid-feedback" v-if="errors.formula !== ''">
                        {{ errors.formula }}
                    </div>
                </div>
            </div>

            <div class="col-6 mb-3" v-if="clients">
                <label class="form-label">Client's Data Blend to Validate Against</label>
                <accordion-select 
                    id="validate_data_blend" 
                    :options="formattedClients"
                    v-model="local_mf_selected.validate_data_blend"
                    @update:modelValue="validateFormula"
                    :splitText="false"
                />
            </div>
        </div>

        <div class="row mb-3">
            <div class="col">
                <div class="alert alert-danger mb-3" v-if="form.error">
                    There was an error saving the {{ type }} information.
                </div>
                <div class="alert alert-warning mb-3" v-if="local_mf_selected.id > 0">
                    Warning: Editing this {{ type.toLowerCase() }} will change it across all reports it's used in.
                </div>
                <!-- Update Button -->
                <button 
                    type="submit" 
                    class="btn btn-primary" 
                    id="save-button"
                    @click.prevent="update"
                    :disabled="form.busy || is_validating"
                >
                    <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> {{ local_mf_selected.id > 0 ? "Update" : "Save" }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import vSelect from "vue3-select";
import AccordionSelect from '../../components/AccordionSelect.vue';

export default defineComponent({
    name: 'MetricFilterBuilder',
    components: {
        vSelect,
        AccordionSelect
    },
    props: {
        data_blend: {
            type: Object,
            required: true
        },
        clients: {
            type: Array,
            default: () => []
        },
        type: {
            type: String,
            required: true
        },
        modal_id: {
            type: String,
            required: true
        },
        default_metric_format: {
            type: String,
            default: null
        },
        client_id: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            local_mf_selected: {
                id: -1,
                admin_name: '',
                name: '',
                type: this.type ? this.type.toLowerCase() : '',
                data_blend_type: this.data_blend ? this.data_blend.type : '',
                data_blend_id: this.clientSelected() ? (this.data_blend ? this.data_blend.id : null) : null,
                description: '',
                importance: '',
                formula: '',
                format: this.default_metric_format ? this.default_metric_format : null,
                color: '*',
                validate_data_blend: null
            },
            errors: {
                name: '',
                admin_name: '',
                format: '',
                color: '',
                formula: ''
            },
            form: {
                busy: false,
                error: false
            },
            is_validating: false,
            is_loading: false,
            local_columns: [],
            unfiltered_local_columns: [],
            column_selected: null,
            expanded_categories: [],
            expanded_subcategories: [],
            validate_data_blend_formatted: null
        };
    },
    mounted() {
        this.loadColumnOptions();
    },
    updated() {
        this.resizeTextarea();

        // hide any tooltips shown
        document.querySelectorAll('.tooltip').forEach(e => e.remove());
    },
    watch: {
        type(val) {
            this.local_mf_selected.type = this.type.toLowerCase();
        },
        client_id(val) {
            // Only load column options if client_id is valid and data_blend exists
            if (val !== null && val !== undefined && this.data_blend) {
                this.loadColumnOptions();
            }
        },
        data_blend(val) {
            // Only proceed if data_blend is valid
            if (val) {
                const isClientSelected = this.clientSelected();
                this.local_mf_selected.data_blend_id = (!isClientSelected ? null : this.data_blend.id);
                this.local_mf_selected.data_blend_type = this.data_blend.type;
                this.loadColumnOptions();
            } else {
                // Reset data blend related properties if data_blend is null
                this.local_mf_selected.data_blend_id = null;
                this.local_mf_selected.data_blend_type = '';
                this.local_columns = [];
            }
        },
        default_metric_format(val) {
            if (this.default_metric_format) {
                this.local_mf_selected.format = 'percent';
            }
        },
        local_mf_selected: {
            handler(newVal) {
                if (newVal && newVal.validate_data_blend && newVal.validate_data_blend.id !== undefined) {
                    this.validate_data_blend_formatted = {
                        value: newVal.validate_data_blend.id ? newVal.validate_data_blend.id.toString() : '',
                        text: newVal.validate_data_blend.name || '',
                        category: '',
                        subcategory: ''
                    };
                } else {
                    this.validate_data_blend_formatted = null;
                }
            },
            immediate: true,
            deep: true
        }
    },
    computed: {
        formattedClients() {
            if (!this.clients || !Array.isArray(this.clients)) {
                return [];
            }
            return this.clients.map(client => ({
                value: client.id ? client.id.toString() : '',
                text: client.name || '',
                category: '',
                subcategory: ''
            }));
        }
    },
    methods: {
        setMetricFilter(mf) {
            if (mf === null) {
                this.local_mf_selected = this.getNewMetricFilter();
            } else {
                this.local_mf_selected = mf;
            }

            this.clearErrors();

            //Clear or revalidate the formula
            if (this.local_mf_selected.formula !== "") {
                this.validateFormula();
            } else {
                this.clearErrorForField('formula');
            }
        },
        clientSelected() {
            // client_id is passed in from report_builder, client_id is null means it is a template report
            if (this.client_id === null) {
                return null;
            }
            // Check if data_blend exists before accessing its properties
            if (!this.data_blend) {
                return false;
            }
            return this.data_blend.client_id !== null && this.data_blend.client_id > 0;
        },
        selectFormat(formatType) {
            this.clearErrorForField('format');
            if (formatType === "money") {
                this.local_mf_selected.color = 'green';
            } else if (formatType === "percent") {
                this.local_mf_selected.color = 'blue';
            } else if (formatType === "number") {
                this.local_mf_selected.color = 'yellow';
            } else if (formatType === "date") {
                this.local_mf_selected.color = 'blue';
            } else {
                this.local_mf_selected.color = 'purple';
            }
        },
        getNewMetricFilter() {
            // Check if data_blend exists before accessing its properties
            const isClientSelected = this.clientSelected();
            const hasDataBlend = !!this.data_blend;

            return {
                id: -1,
                admin_name: '',
                name: '',
                type: this.type ? this.type.toLowerCase() : '',
                data_blend_type: hasDataBlend ? this.data_blend.type : '',
                data_blend_id: (!isClientSelected || !hasDataBlend ? null : this.data_blend.id),
                description: '',
                importance: '',
                formula: '',
                format: this.default_metric_format ? this.default_metric_format : null,
                color: '*',
                validate_data_blend: null
            };
        },
        clearErrors() { //reset errors to empty strings, except formula errors
            this.form.error = false;
            this.errors.admin_name = '';
            this.errors.name = '';
            this.errors.format = '';
            this.errors.color = '';
        },
        clearErrorForField(field_name) {
            this.form.error = false;
            this.errors[field_name] = '';
        },
        hasErrors() { //validate the form
            if (this.local_mf_selected.admin_name === null || this.local_mf_selected.admin_name === "") {
                this.errors.admin_name = "A name is required.";
            } else {
                this.errors.admin_name = "";
            }
            if (this.local_mf_selected.name === null || this.local_mf_selected.name === "") {
                this.errors.name = "A display name is required.";
            } else {
                this.errors.name = "";
            }

            if (this.local_mf_selected.type === 'metric' && (this.local_mf_selected.format === null || this.local_mf_selected.format === "")) {
                this.errors.format = "A format type is required for a metric.";
            } else {
                this.errors.format = "";
            }

            if (this.local_mf_selected.type === 'metric' && (this.local_mf_selected.color === null || this.local_mf_selected.color === "")) {
                this.errors.color = "A color is required for a metric.";
            } else {
                this.errors.color = "";
            }

            if (this.local_mf_selected.formula === null || this.local_mf_selected.formula === "") {
                this.errors.formula = "A formula is required.";
            }

            return this.errors.admin_name !== "" || this.errors.format !== "" || this.errors.color !== "" || this.errors.formula !== "";
        },
        validateFormula() {
            this.clearErrorForField('formula');

            // Check if formula is empty
            if (!this.local_mf_selected.formula || this.local_mf_selected.formula.trim() === '') {
                this.errors.formula = "A formula is required.";
                return;
            }

            // Check if data_blend exists
            if (!this.data_blend) {
                this.errors.formula = "No data blend selected. Please select a data blend first.";
                return;
            }

            let mf = JSON.parse(JSON.stringify(this.local_mf_selected));

            // Handle validation with a specific client's data blend
            if (this.local_mf_selected.validate_data_blend && 
                this.local_mf_selected.validate_data_blend.data_blends && 
                Array.isArray(this.local_mf_selected.validate_data_blend.data_blends)) {

                let foundMatchingBlend = false;
                for (let i = 0; i < this.local_mf_selected.validate_data_blend.data_blends.length; i++) {
                    if (this.local_mf_selected.validate_data_blend.data_blends[i].type === this.local_mf_selected.data_blend_type) {
                        mf.data_blend_id = this.local_mf_selected.validate_data_blend.data_blends[i].id;
                        foundMatchingBlend = true;
                        break;
                    }
                }

                if (!foundMatchingBlend) {
                    this.errors.formula = "No matching data blend type found in the selected client.";
                    return;
                }
            }
            // Use the current data blend
            else if (this.data_blend && this.data_blend.id) {
                mf.data_blend_id = this.data_blend.id;
            }
            else {
                this.errors.formula = "No valid data blend selected.";
                return;
            }

            // return true if formula is valid, return false if formula is invalid
            this.is_validating = true;
            window.axios.post('/api/metric_filter/validate', mf)
                .then(() => {
                    this.is_validating = false;
                    this.errors.formula = "";
                }).catch(error => {
                    this.is_validating = false;
                    if (error.response && error.response.data) {
                        let err = error.response.data.message;
                        if (err && err.indexOf(":") === -1) {
                            err = "Error: " + err;
                        }
                        this.errors.formula = err;
                    } else {
                        this.errors.formula = "An error occurred during validation.";
                        console.error("Validation error:", error);
                    }
                });
        },
        loadColumnOptions() {
            // Check if data_blend exists before accessing its properties
            if (!this.data_blend) {
                this.is_loading = false;
                return;
            }

            const data = {
                include_trans: true,
                source: 'data_blend',
                data_source_id: this.data_blend.id
            };
            this.is_loading = true;
            window.axios.post('/api/bigquery/get_db_columns', data)
                .then(response => {
                    let cols = response.data.columns;
                    
                    // Filter out custom struct columns for template data source
                    for (let i = cols.length - 1; i >= 0; i--) {
                        if (!this.clientSelected() && cols[i].value.indexOf(".custom") > -1) {
                            cols.splice(i, 1);
                        }
                        else if (cols[i].value.indexOf(".") === -1) {
                            cols[i].value = "c." + cols[i].value;
                        }
                    }

                    // Sort columns to ensure proper nesting
                    cols.sort((a, b) => {
                        // First sort by category
                        if(a.value.indexOf(".") == -1 && b.value.indexOf(".") != -1)
                            return -1;
                        else if(a.value.indexOf(".") != -1 && b.value.indexOf(".") == -1)
                            return 1;
                        
                        // If same category, sort by subcategory presence
                        if(a.value.indexOf(".") != -1 && b.value.indexOf(".") != -1) {
                            const aParts = a.value.split(".");
                            const bParts = b.value.split(".");
                            
                            // If same category
                            if(aParts[0] === bParts[0]) {
                                // If one has subcategory and other doesn't, put the one without subcategory first
                                if(aParts.length === 2 && bParts.length > 2) return -1;
                                if(aParts.length > 2 && bParts.length === 2) return 1;
                                
                                // If both have subcategories, sort by subcategory
                                if(aParts.length > 2 && bParts.length > 2) {
                                    return aParts[1].localeCompare(bParts[1]);
                                }
                            }
                            return aParts[0].localeCompare(bParts[0]);
                        }
                        
                        return a.value.localeCompare(b.value);
                    });

                    // Format columns with categories and subcategories
                    let formatted_cols = [];
                    let current_category = null;
                    let current_subcategory = null;

                    for (let i = 0; i < cols.length; i++) {
                        let col = cols[i];
                        let category = null;
                        let subcategory = null;
                        let text = col.text;

                        if (col.value.indexOf(".") != -1) {
                            let parts = col.value.split(".");
                            category = this.propercase(parts[0]);
                            
                            if (parts.length > 2) {
                                subcategory = this.propercase(parts[1]);
                                // For 3+ levels, join remaining parts with ›
                                text = this.propercase(parts.slice(2).join(' › '));
                            } else {
                                text = this.propercase(parts[1]);
                            }

                            // Add category divider if category changes
                            if (category != current_category) {
                                formatted_cols.push({
                                    value: "DIVIDER-" + category,
                                    category: category,
                                    text: "",
                                    subcategory: null
                                });
                                current_subcategory = null; // Reset subcategory when category changes
                            }

                            // Add subcategory divider if subcategory changes
                            if (subcategory && subcategory != current_subcategory) {
                                formatted_cols.push({
                                    value: "DIVIDER-" + category + "-" + subcategory,
                                    category: category,
                                    subcategory: subcategory,
                                    text: ""
                                });
                            }
                        }

                        current_category = category;
                        current_subcategory = subcategory;

                        formatted_cols.push({
                            value: col.value,
                            text: text,
                            category: category,
                            subcategory: subcategory
                        });
                    }

                    this.local_columns = formatted_cols;
                    this.unfiltered_local_columns = JSON.parse(JSON.stringify(formatted_cols));
                    this.is_loading = false;
                })
                .catch(error => {
                    console.error("Error loading column options:", error);
                    this.is_loading = false;
                });
        },
        resizeTextarea() {
            const element = document.querySelectorAll('textarea')[0];
            if (element && element.style) {
                element.style.height = '';
                element.style.height = Math.max(element.offsetHeight, element.scrollHeight) + "px";
            }
        },
        addColumnToText(valueToAdd) {
            const textField = document.getElementById(this.modal_id).getElementsByClassName('formula-input')[0];

            // Get the cursor's position
            const startPos = textField.selectionStart;
            const endPos = textField.selectionEnd;

            // Get the current value of the text field
            const currentValue = textField.value;

            // Construct the new value with the added text
            const newValue = currentValue.substring(0, startPos) + valueToAdd.value + currentValue.substring(endPos);

            // Set the new value to the text field
            textField.value = newValue;

            // Move the cursor after the added text
            const newCursorPos = startPos + valueToAdd.value.length;
            textField.setSelectionRange(newCursorPos, newCursorPos);

            this.local_mf_selected.formula = newValue;

            this.validateFormula();
        },
        expandCategory(cat, expand) {
            if (!cat) return false;
            
            const isExpanded = this.expanded_categories.includes(cat);
            const shouldExpand = expand === true || !isExpanded;

            // Update state arrays immediately for reactive updates
            if (shouldExpand && !this.expanded_categories.includes(cat)) {
                this.expanded_categories.push(cat);
            } else if (!shouldExpand) {
                this.expanded_categories = this.expanded_categories.filter(c => c !== cat);
                // Remove all subcategories of this category
                this.expanded_subcategories = this.expanded_subcategories.filter(sc => !sc.startsWith(cat + '-'));
            }

            // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
            this.$nextTick(() => {
                // Find all elements related to this category
                const categoryHeader = document.querySelector(`div[data-category='${cat}']`);
                if (!categoryHeader) return;

                // Update category header
                const categoryIcon = categoryHeader.querySelector('.fa-caret-right, .fa-caret-down');
                if (categoryIcon) {
                    categoryIcon.classList.toggle('fa-caret-right', !shouldExpand);
                    categoryIcon.classList.toggle('fa-caret-down', shouldExpand);
                }
                categoryHeader.dataset.isexpanded = shouldExpand;

                // Find and update all related subcategory headers
                const subcategoryHeaders = document.querySelectorAll(`div[data-subcategory='${cat}'].subcategory-header`);
                subcategoryHeaders.forEach(header => {
                    header.style.display = shouldExpand ? "block" : "none";
                    if (!shouldExpand) {
                        header.dataset.isexpanded = "false";
                        const subcategoryIcon = header.querySelector('.fa-caret-right, .fa-caret-down');
                        if (subcategoryIcon) {
                            subcategoryIcon.classList.add('fa-caret-right');
                            subcategoryIcon.classList.remove('fa-caret-down');
                        }
                    }
                });
            });

            return false;
        },
        expandSubcategory(cat, subcat, expand) {
            if (!cat || !subcat) return false;

            const subcategoryKey = `${cat}-${subcat}`;
            const isExpanded = this.expanded_subcategories.includes(subcategoryKey);
            const shouldExpand = expand === true || !isExpanded;

            // Update state array immediately for reactive updates
            if (shouldExpand && !this.expanded_subcategories.includes(subcategoryKey)) {
                this.expanded_subcategories.push(subcategoryKey);
            } else if (!shouldExpand) {
                this.expanded_subcategories = this.expanded_subcategories.filter(sc => sc !== subcategoryKey);
            }

            // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
            this.$nextTick(() => {
                const header = document.querySelector(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}']`);
                if (!header) return;

                // Update header
                const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                if (icon) {
                    icon.classList.toggle('fa-caret-right', !shouldExpand);
                    icon.classList.toggle('fa-caret-down', shouldExpand);
                }
                header.dataset.isexpanded = shouldExpand;

                // Find and update all options under this subcategory
                const options = document.querySelectorAll(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}'] + div.suboption`);
                options.forEach(option => {
                    option.style.display = shouldExpand ? "block" : "none";
                });
            });

            return false;
        },
        isSubcategoryVisible(category, subcategory) {
            // On initial load, subcategory should only be visible if its parent category is expanded
            // If the category is not expanded, hide all its subcategories
            return this.expanded_categories.includes(category);
        },
        shouldShowOption(category, subcategory) {
            let classes = [];
            
            if (!category) {
                classes.push('show');
            } else {
                classes.push('category-option');
                const categoryExpanded = this.expanded_categories.includes(category);
                if (!categoryExpanded) return classes.join(' ');
                
                if (!subcategory) {
                    classes.push('show');
                } else {
                    classes.push('subcategory-option');
                    const subcategoryKey = `${category}-${subcategory}`;
                    if (this.expanded_subcategories.includes(subcategoryKey)) {
                        classes.push('show');
                    }
                }
            }
            
            return classes.join(' ');
        },
        handleHover(event) {
            // Remove selected and highlight classes from all options
            const allOptions = document.querySelectorAll('.vs__dropdown-option');
            allOptions.forEach(option => {
                option.classList.remove('vs__dropdown-option--selected', 'vs__dropdown-option--highlight');
            });
        },
        propercase(str) {
            if (!str) return '';
            return str.replace(/\w\S*/g, function(txt) {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
            });
        },
        handleColumnDisplayOpen() {
            // Reset expanded states
            this.expanded_categories = [];
            this.expanded_subcategories = [];

            // Ensure all categories and subcategories are collapsed
            this.$nextTick(() => {
                // Find all category headers and collapse them
                const categoryHeaders = document.querySelectorAll('.category-header');
                categoryHeaders.forEach(header => {
                    const category = header.dataset.category;
                    if (category) {
                        const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                        if (icon) {
                            icon.classList.add('fa-caret-right');
                            icon.classList.remove('fa-caret-down');
                        }
                        header.dataset.isexpanded = "false";
                    }
                });

                // Find all subcategory headers and collapse them
                const subcategoryHeaders = document.querySelectorAll('.subcategory-header');
                subcategoryHeaders.forEach(header => {
                    const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                    if (icon) {
                        icon.classList.add('fa-caret-right');
                        icon.classList.remove('fa-caret-down');
                    }
                    header.dataset.isexpanded = "false";
                });
            });
        },
        fetchOptions(search, loading) {
            //Reset the array
            this.local_columns = JSON.parse(JSON.stringify(this.unfiltered_local_columns || this.local_columns));

            if (search === "") {
                return;
            }

            this.is_loading = true;

            //Look at each column
            for (let i = this.local_columns.length - 1; i >= 0; i--) {
                const item = this.local_columns[i];
                
                // Check if search matches text, category, or subcategory
                const searchLower = search.toLowerCase();
                const textMatch = item.text && item.text.toLowerCase().indexOf(searchLower) !== -1;
                const categoryMatch = item.category && item.category.toLowerCase().indexOf(searchLower) !== -1;
                const subcategoryMatch = item.subcategory && item.subcategory.toLowerCase().indexOf(searchLower) !== -1;
                
                // Keep item if it matches search in any field, or if it's a category/subcategory divider
                if (!textMatch && !categoryMatch && !subcategoryMatch && item.text !== "") {
                    this.local_columns.splice(i, 1);
                }
            }

            //Get the remaining categories and subcategories
            const cats = [];
            const subcats = new Map(); // Map to track subcategories and their matching options
            
            for (let i = 0; i < this.local_columns.length; i++) {
                const item = this.local_columns[i];
                if (item.category !== null && item.text !== "") {
                    if (!cats.includes(item.category)) {
                        cats.push(item.category);
                    }

                    // Track subcategories that have matching options
                    if (item.subcategory) {
                        const key = `${item.category}-${item.subcategory}`;
                        if (!subcats.has(key)) {
                            subcats.set(key, false);
                        }
                        subcats.set(key, true);
                    }
                }
            }

            //Remove subcategory headers that have no matching options
            for (let i = this.local_columns.length - 1; i >= 0; i--) {
                const item = this.local_columns[i];
                if (item.text === "" && item.subcategory) {
                    const key = `${item.category}-${item.subcategory}`;
                    if (!subcats.get(key)) {
                        this.local_columns.splice(i, 1);
                    }
                }
            }

            //Remove category headers that have no matching options
            for (let i = this.local_columns.length - 1; i >= 0; i--) {
                const item = this.local_columns[i];
                if (item.text === "" && item.category !== null && !cats.includes(item.category)) {
                    this.local_columns.splice(i, 1);
                }
            }

            //Expand the categories and subcategories
            for (let i = 0; i < cats.length; i++) {
                this.expandCategory(cats[i], true);
                // Find and expand all subcategories for this category
                subcats.forEach((hasMatches, key) => {
                    const [category, subcategory] = key.split('-');
                    if (category === cats[i] && hasMatches) {
                        this.expandSubcategory(category, subcategory, true);
                    }
                });
            }

            this.is_loading = false;
            return true;
        },
        update() {
            this.clearErrors();
            if (this.hasErrors()) {
                return;
            }
            this.form.busy = true;
            const self = this;

            // put a new object together
            if (this.local_mf_selected.id === -1 || this.local_mf_selected.id === null) {
                window.axios.post('/api/metric_filter', this.local_mf_selected)
                    .then(response => {
                        self.form.busy = false;
                        self.$emit('update', response.data.metric_filter);
                        self.local_mf_selected = self.getNewMetricFilter();
                    }).catch(error => {
                        self.form.error = true;
                        setTimeout(() => { self.form.error = false; }, 10000);
                    });
            } else {
                window.axios.put('/api/metric_filter/' + this.local_mf_selected.id, this.local_mf_selected)
                    .then(response => {
                        self.form.busy = false;
                        self.$emit('update', response.data.metric_filter);
                        self.local_mf_selected = self.getNewMetricFilter();
                    }).catch(error => {
                        self.form.error = true;
                        setTimeout(() => { self.form.error = false; }, 10000);
                    });
            }
        },
        getDisplayText(text) {
            if (!text) return '';
            
            // Try different possible separators
            const separators = [' › ', ' > ', ' - ', ': '];
            for (const separator of separators) {
                if (text.includes(separator)) {
                    const parts = text.split(separator);
                    return parts.pop();
                }
            }
            
            return text;
        },
    }
});
</script>
